import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ColorRing } from 'react-loader-spinner';
import { Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import { AddPlanFormValues } from 'api/InteractiveMap/types';
import usePostNewPlan from 'hooks/InteractiveMap/usePostNewPlan';
import { MLInput } from 'components/Input/Input';

const CreatePlanPage = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { createNewPlan, isLoading } = usePostNewPlan();
  const { control, handleSubmit, reset, setError } = useForm<AddPlanFormValues>({
    defaultValues: {
      title: '',
      area: '',
    },
  });

  useEffect(() => {
    const noPlanRedirect = searchParams.get('no_plan_redirect');
    if (!noPlanRedirect) {
      navigate(`/object/interactive-map/${slug}`);
    }
  }, [searchParams, slug, navigate]);

  const onReset = () => {
    reset();
    navigate(`/object/interactive-map/${slug}`);
  };

  const onAddNewPlan = (data: AddPlanFormValues) => {
    createNewPlan(
      {
        plan: data,
        slug: slug as string,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('AddSiteMap.planAdded'), { variant: 'success' });
          onReset();
        },
        onError: (error: any) => {
          if (error.response?.status === 413) {
            setError('uploaded_file', { message: t('AddSiteMap.tooLargeFile') });
            enqueueSnackbar(t('AddSiteMap.tooLargeFile'), { variant: 'error' });
            return;
          }

          if (error.response?.data?.name) {
            setError('title', { message: t('AddSiteMap.titleAlreadyExists') });
          }
          if (error.response?.data?.uploaded_file) {
            setError('uploaded_file', { message: t('AddSiteMap.tooLargeFile') });
          }
        },
      },
    );
  };

  return (
    <div className="card position-relative">
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            background: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ColorRing
            visible
            height="80"
            width="80"
            ariaLabel="color-ring-loading"
            wrapperStyle={{}}
            wrapperClass="color-ring-wrapper"
            colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
          />
        </div>
      )}
      <div className="card-header">
        <h4 className="card-title mb-0">{t('AddSiteMap.addSitePlan')}</h4>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-12">
            <Controller
              control={control}
              name="title"
              rules={{
                required: t('AddSiteMap.requiredField'),
                minLength: { value: 1, message: t('AddSiteMap.requiredField') },
              }}
              render={({ field, fieldState: { error } }) => (
                <MLInput label={t('AddSiteMap.planName')} error={error?.message} {...field} required />
              )}
            />
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12">
            <Controller
              control={control}
              name="area"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  label={t('AddSiteMap.areaOptional')}
                  error={error?.message}
                  {...field}
                  placeholder={t('AddSiteMap.areaPlaceholder')}
                />
              )}
            />
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12">
            <Controller
              control={control}
              name="uploaded_file"
              rules={{
                required: t('AddSiteMap.requiredField'),
              }}
              render={({ field: { onChange }, fieldState: { error } }) => (
                <MLInput
                  label={t('AddSiteMap.uploadPlan')}
                  error={error?.message}
                  type="file"
                  onChange={e => {
                    const file = e.target.files?.[0] || null;
                    onChange(file);
                  }}
                />
              )}
            />
          </div>
        </div>
      </div>
      <div className="card-footer">
        <div className="row">
          <div className="col-12 text-end">
            <Button
              type="button"
              className="btn btn-ghost-danger"
              disabled={isLoading}
              onClick={() => {
                onReset();
              }}
            >
              {t('Modals.delete-modal.cancel')}
            </Button>{' '}
            <Button
              type="button"
              className="btn btn-ghost-success"
              onClick={handleSubmit(onAddNewPlan)}
              disabled={isLoading}
            >
              {t('AddSiteMap.addSitePlan')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePlanPage;
